import { 
  collection, 
  addDoc, 
  query, 
  where, 
  getDocs, 
  updateDoc,
  doc,
  orderBy,
  limit,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../firebase/config';
import systemLogger from './systemLogger';
import analyticsService from './analyticsService';

class SecurityService {
  constructor() {
    this.failedLoginAttempts = new Map();
    this.suspiciousActivities = new Map();
    this.locationHistory = new Map();
    this.isInitialized = false;
  }

  // Initialize security monitoring
  async init(userId) {
    if (this.isInitialized) return;
    
    this.userId = userId;
    this.isInitialized = true;
    
    // Load user's security history
    await this.loadSecurityHistory(userId);
  }

  // Load existing security events for analysis
  async loadSecurityHistory(userId) {
    try {
      const securityEventsRef = collection(db, 'securityEvents');
      const q = query(
        securityEventsRef,
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(50)
      );
      
      const snapshot = await getDocs(q);
      snapshot.forEach(doc => {
        const event = doc.data();
        this.analyzeHistoricalEvent(event);
      });
    } catch (error) {
      console.error('Failed to load security history:', error);
    }
  }

  // Analyze historical security events
  analyzeHistoricalEvent(event) {
    const { eventType, details, userId } = event;
    
    switch (eventType) {
      case 'failed_attempt':
        const userAttempts = this.failedLoginAttempts.get(userId) || [];
        userAttempts.push(event);
        this.failedLoginAttempts.set(userId, userAttempts);
        break;
        
      case 'login_anomaly':
        if (details.location) {
          const locations = this.locationHistory.get(userId) || [];
          locations.push(details.location);
          this.locationHistory.set(userId, locations);
        }
        break;
    }
  }

  // Track failed login attempt
  async trackFailedLogin(userId, reason, metadata = {}) {
    const eventData = {
      userId,
      eventType: 'failed_attempt',
      severity: 'medium',
      details: {
        reason,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        location: await this.getLocation(),
        timestamp: new Date().toISOString(),
        ...metadata,
      },
      resolved: false,
      timestamp: Timestamp.now(),
    };

    try {
      // Store security event
      await addDoc(collection(db, 'securityEvents'), eventData);
      
      // Update failed attempts tracking
      const attempts = this.failedLoginAttempts.get(userId) || [];
      attempts.push(eventData);
      this.failedLoginAttempts.set(userId, attempts);

      // Check for suspicious patterns
      await this.checkFailedLoginPattern(userId, attempts);

      // Log to system
      await systemLogger.logSecurityEvent('failed_login', 'medium', {
        userId,
        reason,
        attemptCount: attempts.length,
      });

    } catch (error) {
      console.error('Failed to track failed login:', error);
    }
  }

  // Check for suspicious failed login patterns
  async checkFailedLoginPattern(userId, attempts) {
    const recentAttempts = attempts.filter(
      attempt => Date.now() - attempt.timestamp.toMillis() < 3600000 // Last hour
    );

    if (recentAttempts.length >= 5) {
      await this.createSecurityAlert(userId, 'multiple_failed_logins', 'high', {
        attemptCount: recentAttempts.length,
        timeWindow: '1 hour',
        lastAttempt: recentAttempts[recentAttempts.length - 1],
      });

      // Update user risk score
      await analyticsService.updateRiskScore(['multiple_failed_logins']);
    }
  }

  // Track successful login and check for anomalies
  async trackSuccessfulLogin(userId, metadata = {}) {
    const currentLocation = await this.getLocation();
    const currentIP = await this.getClientIP();
    
    try {
      // Check for location anomaly
      await this.checkLocationAnomaly(userId, currentLocation, currentIP);
      
      // Check for device anomaly
      await this.checkDeviceAnomaly(userId, navigator.userAgent);
      
      // Clear failed login attempts on successful login
      this.failedLoginAttempts.delete(userId);

      // Log successful login
      await systemLogger.logAuth('login', true, {
        userId,
        location: currentLocation,
        ipAddress: currentIP,
        ...metadata,
      });

    } catch (error) {
      console.error('Failed to track successful login:', error);
    }
  }

  // Check for location anomalies
  async checkLocationAnomaly(userId, currentLocation, currentIP) {
    const userLocations = this.locationHistory.get(userId) || [];
    
    // If this is a completely new location and user has login history
    if (userLocations.length > 0 && !userLocations.includes(currentLocation)) {
      // Check if it's geographically suspicious (simplified check)
      const isSuspicious = await this.isLocationSuspicious(userLocations, currentLocation);
      
      if (isSuspicious) {
        await this.createSecurityAlert(userId, 'login_anomaly', 'high', {
          type: 'location',
          currentLocation,
          previousLocations: userLocations,
          ipAddress: currentIP,
        });

        await analyticsService.updateRiskScore(['suspicious_location']);
      }
    }

    // Update location history
    userLocations.push(currentLocation);
    this.locationHistory.set(userId, userLocations.slice(-10)); // Keep last 10 locations
  }

  // Check for device anomalies — Wave 3: local only (was a Firestore write every login)
  async checkDeviceAnomaly(userId, userAgent) {
    const deviceInfo = this.parseUserAgent(userAgent);
    if (process.env.NODE_ENV === 'development') {
      console.debug('[security:local] device_check', { userId, deviceInfo });
    }
  }

  // Check if location change is suspicious
  async isLocationSuspicious(previousLocations, currentLocation) {
    // Simplified logic - in production you'd use geolocation APIs
    // Check if location is from a different country than usual
    const currentCountry = currentLocation.split(',').pop().trim();
    const previousCountries = previousLocations.map(loc => loc.split(',').pop().trim());
    
    return !previousCountries.includes(currentCountry);
  }

  // Track permission violations
  async trackPermissionViolation(userId, action, resource, metadata = {}) {
    await this.createSecurityAlert(userId, 'permission_violation', 'high', {
      action,
      resource,
      ...metadata,
    });

    await analyticsService.updateRiskScore(['permission_violation']);

    await systemLogger.logSecurityEvent('permission_violation', 'high', {
      userId,
      action,
      resource,
    });
  }

  // Track suspicious activities
  async trackSuspiciousActivity(userId, activity, metadata = {}) {
    const userActivities = this.suspiciousActivities.get(userId) || [];
    const activityData = {
      activity,
      timestamp: Date.now(),
      metadata,
    };
    
    userActivities.push(activityData);
    this.suspiciousActivities.set(userId, userActivities);

    // Check for patterns
    const recentActivities = userActivities.filter(
      act => Date.now() - act.timestamp < 3600000 // Last hour
    );

    if (recentActivities.length >= 3) {
      await this.createSecurityAlert(userId, 'suspicious_activity', 'medium', {
        activities: recentActivities,
        pattern: 'multiple_suspicious_actions',
      });

      await analyticsService.updateRiskScore(['unusual_activity']);
    }
  }

  // Create security alert
  async createSecurityAlert(userId, eventType, severity, details) {
    const alertData = {
      userId,
      eventType,
      severity,
      details: {
        ...details,
        ipAddress: await this.getClientIP(),
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString(),
      },
      resolved: false,
      timestamp: Timestamp.now(),
    };

    try {
      await addDoc(collection(db, 'securityEvents'), alertData);
      
      // Send notification to admins for high severity events
      if (severity === 'high') {
        await this.notifyAdmins(alertData);
      }

    } catch (error) {
      console.error('Failed to create security alert:', error);
    }
  }

  // Notify admins of high-severity security events
  async notifyAdmins(alertData) {
    try {
      // This would typically send notifications to admin users
      // For now, just log to system
      await systemLogger.logSecurityEvent('admin_notification', 'critical', {
        alert: alertData,
        notificationSent: true,
      });
    } catch (error) {
      console.error('Failed to notify admins:', error);
    }
  }

  // Resolve security event
  async resolveSecurityEvent(eventId, resolvedBy, notes = '') {
    try {
      const eventRef = doc(db, 'securityEvents', eventId);
      await updateDoc(eventRef, {
        resolved: true,
        resolvedBy,
        resolvedAt: Timestamp.now(),
        notes,
      });

      await systemLogger.logAdminAction('resolve_security_event', null, {
        eventId,
        resolvedBy,
        notes,
      });

    } catch (error) {
      console.error('Failed to resolve security event:', error);
    }
  }

  // Check if user is currently flagged as high risk
  async isHighRiskUser(userId) {
    try {
      const recentEventsRef = collection(db, 'securityEvents');
      const q = query(
        recentEventsRef,
        where('userId', '==', userId),
        where('resolved', '==', false),
        where('severity', 'in', ['high', 'critical']),
        limit(5)
      );
      
      const snapshot = await getDocs(q);
      return snapshot.size > 0;
    } catch (error) {
      console.error('Failed to check user risk status:', error);
      return false;
    }
  }

  // Get security recommendations for a user
  async getSecurityRecommendations(userId) {
    const recommendations = [];
    
    try {
      // Check for common security improvements
      const failedAttempts = this.failedLoginAttempts.get(userId) || [];
      if (failedAttempts.length > 0) {
        recommendations.push({
          type: 'enable_2fa',
          priority: 'high',
          message: 'Enable two-factor authentication to secure your account',
        });
      }

      const locationChanges = this.locationHistory.get(userId) || [];
      if (locationChanges.length > 3) {
        recommendations.push({
          type: 'review_locations',
          priority: 'medium',
          message: 'Review your recent login locations',
        });
      }

      return recommendations;
    } catch (error) {
      console.error('Failed to get security recommendations:', error);
      return [];
    }
  }

  // Helper methods
  async getClientIP() {
    try {
      // Use a more reliable IP service with better CORS support
      const response = await fetch('https://api.ipify.org?format=json');
      if (!response.ok) throw new Error('IP service unavailable');
      const data = await response.json();
      return data.ip;
    } catch (error) {
      console.warn('Failed to get client IP:', error.message);
      return 'unknown';
    }
  }

  async getLocation() {
    // Always use timezone-based location to avoid CORS and rate limiting issues
    return this.getTimezoneBasedLocation();
  }

  // Get location based on timezone (more reliable fallback)
  getTimezoneBasedLocation() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const parts = timezone.split('/');
      
      if (parts.length >= 2) {
        const region = parts[0].replace('_', ' ');
        const city = parts[1].replace('_', ' ');
        
        // Map common timezone regions to countries
        const regionCountryMap = {
          'America': 'United States',
          'Europe': 'Europe',
          'Asia': 'Asia',
          'Africa': 'Africa',
          'Australia': 'Australia',
          'Pacific': 'Pacific Region',
          'Atlantic': 'Atlantic Region',
          'Indian': 'Indian Ocean Region',
          'Antarctica': 'Antarctica'
        };
        
        const country = regionCountryMap[region] || 'Unknown Region';
        return `${city}, ${country}`;
      }
      
      return 'Unknown Location';
    } catch (error) {
      console.warn('Failed to get timezone-based location:', error.message);
      return 'Unknown Location';
    }
  }

  parseUserAgent(userAgent) {
    return {
      userAgent,
      platform: navigator.platform,
      language: navigator.language,
      cookieEnabled: navigator.cookieEnabled,
      onLine: navigator.onLine,
    };
  }
}

// Create singleton instance
const securityService = new SecurityService();

export default securityService; 